import { Box, Button, Card, styled, useTheme } from '@mui/material';
import { H3, H5, Small } from 'components/Typography';
import {
  Bookmark,
  ChevronRight,
  Copy,
  ExternalLink,
  Send,
  Share2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FlexBox from 'components/flexbox/FlexBox';
import SuccessModal2 from 'components/modal/alerts/SuccessModal2';

const FundingDetailSection = ({
  isTablet,
  isMobile,
  title = '2025년 여성창업 상세페이지 교육 및 컨설팅',
  amount = '9,500만원',
  deadline = '25.01.06',
  institution = '중소벤처기업진흥공단',
  institutionLogo = '/static/logo/SMEs.png',
  tags = ['서울 전체', '소상공인', '고용안정'],
  aiSummary = [
    '신산업 책임보험 최대 700만원/년 내 지원이 제공됩니다.',
    '해외 전시회 참여 1건에 대해 최대 500만원 지원이 제공됩니다.',
    '해외 전시회 참여 1건에 대해 최대 500만원 지원이 제공됩니다.',
    '해외 전시회 참여 1건에 대해 최대 500만원 지원이 제공됩니다.',
    '해외 전시회 참여 1건에 대해 최대 500만원 지원이 제공됩니다.',
  ],
  eligibility = [
    '국민의 90%(건보료 등으로 확정)',
    "2006년 12월 31일 이전 출생자는 '개인별 신청·지급'",
    '미성년자는 주민등록 세대주가 신청·수령(상품권·선불카드 등)',
    "주민등록표에 성인인 구성원이 없는 '미성년 세대주'는 직접 신청가능",
  ],
  supportDetails = [
    {
      title: '2차: 국민의 90%(건보료 등으로 확정)에게 1인당 10만원 추가 지금',
      type: 'main',
    },
    {
      title:
        '지급방식(택 1): 지역사랑상품권(카드·모바일·지류형), 신용·체크카드, 선불카드',
      type: 'main',
    },
    {
      title: '사용지역: 특·광역시 또는 시·군',
      type: 'main',
    },
    {
      title: '사용처',
      type: 'main',
      subItems: [
        '지역사랑상품권: 상품권 가맹점',
        '신용·체크·선불카드: 연 매출액 30억원 이하 매장',
      ],
    },
    {
      title: '사용업종',
      type: 'main',
      subItems: [
        '사용가능 업종: 전통시장, 동네마트, 식당, 의류점, 미용실, 안경점, 교습소·학원, 약국·의원, 프랜차이즈 가맹점',
        '사용불가 업종: 대형마트, 기업형 슈퍼마켓(SSM), 대형전자제품판매점, 백화점, 프랜차이즈 직영점, 유흥·사행업종, 보험업 등',
      ],
    },
    {
      title: '사용기한: 1·2차 지급분 2025. 11. 30.(일)까지 사용',
      type: 'main',
      subItems: ['사용하지 않고 남은 금액은 환불되지 않고 자동소멸'],
    },
  ],
  applicationInfo = [
    {
      title: '온라인',
      type: 'main',
      subItems: [
        '지역사랑 상품권사, 신용·체크카드사 등 앱',
        '신용·체크카드사 홈페이지',
      ],
    },
    {
      title: '방문',
      type: 'main',
      subItems: ['주민센터', '은행 영업점'],
    },
    {
      title: '찾아가는 신청',
      type: 'main',
      subItems: [
        '지자체가 직접 고령자·장애인 등 방문',
        '* 신청 첫 주에는 출생년도 끝자리에 따라 요일제 적용',
        '끝자리: 1·6(월), 2·7(화), 3·8(수), 4·9(목), 5·0(금)',
        '온라인: 주말 신청 가능, 오프라인: 주말 신청 불가',
      ],
    },
  ],
  relatedInfo = '관련 정보 없음',
  originalLink = '#',
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [bookmarked, setBookmarked] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const toggleBookmark = () => {
    setBookmarked(prev => !prev);
  };

  const handleApplyClick = () => {
    setShowSuccessModal(true);
    // window.open(originalLink, '_blank');
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      // 간단한 토스트 메시지나 알림을 표시할 수 있습니다
      alert('URL이 복사되었습니다!');
    } catch (err) {
      console.error('URL 복사 실패:', err);
      alert('URL 복사에 실패했습니다.');
    }
  };

  const shareContent = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `${institution} - ${title}`,
          url: window.location.href,
        });
      } catch (err) {
        console.error('공유 실패:', err);
      }
    } else {
      // Web Share API를 지원하지 않는 경우 URL 복사
      copyUrl();
    }
  };

  const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    padding: 0,
    backgroundColor: theme.palette.primary.lightGrey200,
    borderRadius: 0,
    border: 'none',
  }));

  const renderBulletPoint = (text, isMain = true) => (
    <Box
      display="flex"
      alignItems="flex-start"
      mb={1}
      sx={{ marginLeft: isMain ? 0 : '16px' }}
    >
      <Box
        sx={{
          width: '6px',
          height: '6px',
          backgroundColor: isMain
            ? 'transparent'
            : theme.palette.text.secondary,
          border: isMain ? `1px solid ${theme.palette.text.secondary}` : 'none',
          borderRadius: '50%',
          marginTop: '6px',
          marginRight: '8px',
          flexShrink: 0,
        }}
      />
      <Small
        sx={{
          color: theme.palette.text.primary,
          lineHeight: 1.4,
          fontSize: isMobile ? '14px' : '15px',
        }}
      >
        {text}
      </Small>
    </Box>
  );

  const renderSection = (title, items, isMain = true) => (
    <Box mb={4}>
      <H5
        sx={{
          fontWeight: 'bold',
          fontSize: isMobile ? '16px' : '18px',
          marginBottom: 2,
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </H5>
      {Array.isArray(items) ? (
        items.map((item, index) => {
          if (typeof item === 'string') {
            return renderBulletPoint(item, isMain);
          } else if (item.subItems) {
            return (
              <Box key={index}>
                {renderBulletPoint(item.title, isMain)}
                {item.subItems.map((subItem, subIndex) =>
                  renderBulletPoint(subItem, false)
                )}
              </Box>
            );
          } else {
            return renderBulletPoint(item.title, isMain);
          }
        })
      ) : (
        <Small
          sx={{
            color: theme.palette.text.primary,
            lineHeight: 1.4,
            fontSize: isMobile ? '14px' : '15px',
          }}
        >
          {items}
        </Small>
      )}
    </Box>
  );

  return (
    <StyledCard
      sx={{
        border: 'none',
        paddingBottom: 2.5,
        ...props.sx,
      }}
    >
      <Box
        sx={{
          width: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: isMobile ? '18px' : '26px',
            borderRadius: '24px',
            boxShadow: '1px 4px 6px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Title and Bookmark */}
          <Box mb={3}>
            {/* Institution Name and Icons Row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: isMobile ? 1.5 : 0,
                mb: 2,
              }}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: isMobile ? '44px' : '50px',
                    height: isMobile ? '44px' : '50px',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: isMobile ? '12px' : '16px',
                    flexShrink: 0,
                    border: `1px solid ${theme.palette.primary.borderColor}`,
                    borderRadius: '100px',
                  }}
                >
                  <img
                    src={institutionLogo}
                    alt="institution logo"
                    style={{
                      width: isMobile ? '48px' : '52px',
                      borderRadius: '100px',
                    }}
                  />
                </Box>
                <H3
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    fontSize: isMobile ? '17px' : '18.5px',
                  }}
                >
                  {institution}
                </H3>
              </Box>

              {/* Icon buttons */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 1 : 1.5,
                }}
              >
                {/* URL 복사 버튼 */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      transform: isMobile ? 'none' : 'scale(1.05)',
                    },
                  }}
                  onClick={copyUrl}
                >
                  <Copy
                    size={isMobile ? 19 : 21}
                    color={theme.palette.primary.greytxt}
                  />
                </Box>

                {/* 공유 버튼 */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      transform: isMobile ? 'none' : 'scale(1.05)',
                    },
                  }}
                  onClick={shareContent}
                >
                  <Share2
                    size={isMobile ? 19 : 21}
                    color={theme.palette.primary.greytxt}
                  />
                </Box>

                {/* 북마크 버튼 */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      transform: isMobile ? 'none' : 'scale(1.05)',
                    },
                  }}
                  onClick={toggleBookmark}
                >
                  <Bookmark
                    size={isMobile ? 19 : 21}
                    color={
                      bookmarked
                        ? theme.palette.primary.mainBlue
                        : theme.palette.primary.greytxt
                    }
                    fill={bookmarked ? theme.palette.primary.mainBlue : 'none'}
                  />
                </Box>
              </Box>
            </Box>

            {/* Tags and Button Row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: 2,
                width: '100%',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {tags.map((tag, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: '#ffffff',
                      color: theme.palette.primary.mainBlue,
                      padding: isMobile ? '2px 8px' : '4px 12px',
                      borderRadius: '100px',
                      fontSize: isMobile ? '12px' : '13px',
                      fontWeight: '500',
                      border: `1px solid ${theme.palette.primary.grey10}`,
                    }}
                  >
                    {tag}
                  </Box>
                ))}
              </Box>

              {/* 신청하기 Button */}
              <Button
                sx={{
                  background:
                    'linear-gradient(90deg, #3F68FF 0%, #9A68FF 100%)',
                  color: '#fff',
                  borderRadius: '25px',
                  padding: isMobile ? '6px 14px' : '8px 16px',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  textTransform: 'none',
                  border: 'none',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  alignSelf: isMobile ? 'flex-start' : 'auto',
                  '&:hover': {
                    background:
                      'linear-gradient(40deg, #3F68FF 0%, #9A68FF 100%)',
                    boxShadow: '0 6px 16px rgba(79, 70, 229, 0.2)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={handleApplyClick}
              >
                <Send
                  size={isMobile ? 16 : 18}
                  style={{
                    filter: 'brightness(0) invert(1)',
                  }}
                />
                신청하기
              </Button>
            </Box>
          </Box>

          {/* Key Information Panel */}
          <Box
            sx={{
              backgroundColor: '#fcfcfc',
              padding: isMobile ? '0px' : '20px',
              borderRadius: '24px',
              border: isMobile
                ? 'none'
                : `1.5px solid ${theme.palette.primary.grey10}`,
              marginBottom: 4,
            }}
          >
            {/* 지원 금액 */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Small
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: isMobile ? '14px' : '15px',
                }}
              >
                지원 금액
              </Small>
              <Small
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  fontSize: isMobile ? '14px' : '15px',
                }}
              >
                {amount}
              </Small>
            </Box>

            {/* 신청 기간 */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Small
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: isMobile ? '14px' : '15px',
                }}
              >
                신청 기간
              </Small>
              <Small
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  fontSize: isMobile ? '14px' : '15px',
                }}
              >
                {deadline}
              </Small>
            </Box>

            {/* 정책 기관 */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Small
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: isMobile ? '14px' : '15px',
                }}
              >
                정책 기관
              </Small>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: isMobile ? '32px' : '36px',
                    height: isMobile ? '32px' : '36px',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    borderRadius: '50%',
                    border: `1px solid ${theme.palette.primary.borderColor}`,
                  }}
                >
                  <img
                    src={institutionLogo}
                    alt="institution logo"
                    style={{
                      width: isMobile ? '28px' : '32px',
                      height: isMobile ? '28px' : '32px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
                <Small
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    fontSize: isMobile ? '14px' : '15px',
                  }}
                >
                  {institution}
                </Small>
              </Box>
            </Box>

            <Box mt={isMobile ? 2 : 2} sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  backgroundColor: isMobile ? '#fcfcfc' : '#ffffff',
                  padding: '16px',
                  borderRadius: '24px',
                  boxShadow: isMobile
                    ? 'none'
                    : theme.palette.shadows.cardBoxShadow,
                  border: isMobile
                    ? `1px solid ${theme.palette.primary.grey10}`
                    : 'none',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={isMobile ? 1 : 1.5}
                >
                  <Box
                    sx={{
                      width: isMobile ? '24px' : '28px',
                      height: isMobile ? '24px' : '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/static/icons/magic-wand.svg"
                      style={{
                        width: isMobile ? '26px' : '32px',
                        height: isMobile ? '26px' : '32px',
                      }}
                      alt="magic wand"
                    />
                  </Box>
                  <H5
                    sx={{
                      color: theme.palette.primary.mainBlue,
                      fontSize: isMobile ? '16px' : '18px',
                    }}
                  >
                    AI 한줄 요약
                  </H5>
                </Box>

                {aiSummary.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="flex-start"
                    mb={0.5}
                  >
                    <Box
                      sx={{
                        width: '4px',
                        height: '4px',
                        backgroundColor: theme.palette.text.secondary,
                        borderRadius: '50%',
                        marginTop: '6px',
                        marginRight: '8px',
                        flexShrink: 0,
                      }}
                    />
                    <Small
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.4,
                        fontSize: isMobile ? '14px' : '15px',
                      }}
                    >
                      {item}
                    </Small>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* AI Summary Section */}

          {/* Eligibility Section */}
          {renderSection('지원 대상', eligibility)}

          {/* Support Details Section */}
          {renderSection('지원 내용', supportDetails)}

          {/* Related Information Section */}
          {renderSection('관련 정보', relatedInfo)}

          {/* Application Information Section */}
          {renderSection('신청 정보', applicationInfo)}

          {/* Footer with Original Link */}
          <FlexBox
            width="100%"
            justifyContent={'flex-end'}
            pt={isTablet ? 2 : 3}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                cursor: 'pointer',
                color: theme.palette.primary.mainBlue,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={() => window.open(originalLink, '_blank')}
            >
              <ExternalLink size={16} />
              <Small
                sx={{
                  color: theme.palette.primary.mainBlue,
                  fontSize: isMobile ? '13px' : '14px',
                }}
              >
                원문 보기
              </Small>
              <ChevronRight size={16} color={theme.palette.primary.mainBlue} />
            </Box>
          </FlexBox>
        </Box>
      </Box>

      <SuccessModal2
        showModal={showSuccessModal}
        setShowModal={setShowSuccessModal}
        title="신청이 완료되었습니다"
        subtitle="신청 페이지가 새 창에서 열렸습니다."
      />
    </StyledCard>
  );
};

export default FundingDetailSection;
